import * as z from "zod";

/**
 * The two pipeline stages a drill-down can target. `awaiting_processing` is the
 * delivered-but-unprocessed backlog (aged, bucketed); `in_transit` is the
 * incoming-returns context list (no aging). Shared with the frontend so cell /
 * tile clicks pass a typed stage rather than a bare literal.
 */
export const ProcessingPipelineStage = {
  awaitingProcessing: "awaiting_processing",
  inTransit: "in_transit",
} as const;
export type ProcessingPipelineStage =
  (typeof ProcessingPipelineStage)[keyof typeof ProcessingPipelineStage];

/**
 * Awaiting-Processing age buckets, addressable from a merchant-table cell.
 * Values MUST stay equal to `ProcessingAgeBucket` in
 * `@redotech/redo-analytics-managed-returns/business-calendar` — the analytics
 * package owns the aggregation-side const, this is the RPC-boundary mirror the
 * frontend can import without pulling in the backend analytics package.
 */
export const ProcessingPipelineBucket = {
  withinSla: "within_sla",
  overSla: "over_sla",
  critical: "critical",
} as const;
export type ProcessingPipelineBucket =
  (typeof ProcessingPipelineBucket)[keyof typeof ProcessingPipelineBucket];

export const processingPipelineReturnRowSchema = z.object({
  /** Return id (Mongo hex) — shown as the row's identifier and the return-details link. */
  id: z.string(),
  teamId: z.string(),
  teamName: z.string(),
  type: z.string(),
  /** Order name — secondary context beneath the reference. */
  orderName: z.string(),
  customerName: z.string(),
  /** Delivery instant (awaiting-processing rows); null when unknown/undated. */
  deliveredAt: z.string().nullable(),
  /** Created/shipped instant (in-transit rows). */
  createdAt: z.string().nullable(),
  /** Business-day age since delivery; null for in-transit (no clean ship date). */
  ageBusinessDays: z.number().nullable(),
});

export type ProcessingPipelineReturnRow = z.infer<
  typeof processingPipelineReturnRowSchema
>;

export const getProcessingPipelineReturns = {
  name: "getProcessingPipelineReturns" as const,
  input: z.object({
    returnsCenterId: z.string().optional(),
    teamIds: z.array(z.string()).optional(),
    stage: z.enum([
      ProcessingPipelineStage.awaitingProcessing,
      ProcessingPipelineStage.inTransit,
    ]),
    /** Only meaningful for `awaiting_processing`; narrows to one age bucket. */
    bucket: z
      .enum([
        ProcessingPipelineBucket.withinSla,
        ProcessingPipelineBucket.overSla,
        ProcessingPipelineBucket.critical,
      ])
      .optional(),
  }),
  output: z.object({
    returns: z.array(processingPipelineReturnRowSchema),
    /** True when the result was capped — the drawer footnotes the cap. */
    truncated: z.boolean(),
  }),
};

export type GetProcessingPipelineReturnsOutput = z.infer<
  (typeof getProcessingPipelineReturns)["output"]
>;
